import axios, { AxiosInstance, AxiosResponse } from 'axios';

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

interface DashboardStats {
  totalUsers: number;
  totalStudents: number;
  totalEmployers: number;
  totalJobs: number;
  totalCourses: number;
  totalApplications: number;
  totalCertifications: number;
  pendingVerifications: number;
  activeSubscriptions: number;
  revenueThisMonth: number;
  newUsersThisWeek: number;
  completedCoursesThisMonth: number;
}

interface PlatformAnalytics {
  userGrowth: Array<{ date: string; count: number; }>;
  jobPostingTrends: Array<{ date: string; count: number; }>;
  courseCompletionRates: Array<{ courseId: string; title: string; completionRate: number; }>;
  topPerformingStudents: Array<{ studentId: string; name: string; score: number; }>;
  employerEngagement: Array<{ employerId: string; companyName: string; jobPostings: number; }>;
  revenueAnalytics: Array<{ month: string; revenue: number; subscriptions: number; }>;
}

class AdminApiService {
  private api: AxiosInstance;
  private baseURL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

  constructor() {
    this.api = axios.create({
      baseURL: `${this.baseURL}/admin`,
      timeout: 180000, // Increased to 3 minutes for large course uploads
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add the native JWT from localStorage.
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      (error) => {
        if (error.response?.status === 401) {
          // Handle unauthorized access
          window.location.href = '/admin/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // ============================================================================
  // AUTHENTICATION
  // ============================================================================

  /**
   * Authenticate with email/password against the native auth endpoint, store
   * the returned JWT, and return it. Admin authorization is then checked
   * separately via {@link validateAdminAccess}.
   */
  async authenticate(email: string, password: string): Promise<string> {
    const response = await axios.post(`${this.baseURL}/auth/login`, { email, password });
    const token = response.data?.data?.token;
    if (!response.data?.success || !token) {
      throw new Error(response.data?.message || 'Invalid email or password');
    }
    const user = response.data.data.user || {};
    localStorage.setItem('authToken', token);
    localStorage.setItem('authUid', user.id || '');
    localStorage.setItem('authEmail', user.email || '');
    localStorage.setItem('authName', `${user.firstName || ''} ${user.lastName || ''}`.trim());
    return token;
  }

  async validateAdminAccess(token: string): Promise<ApiResponse> {
    try {
      const response = await this.api.post('/login', {}, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      return {
        success: true,
        data: response.data.data,
        message: response.data.message
      };
    } catch (error: any) {
      return { 
        success: false, 
        message: error.response?.data?.message || error.message || 'Admin validation failed' 
      };
    }
  }

  async getAdminProfile(token?: string): Promise<any> {
    const response = await this.api.get('/profile');
    return response.data.data;
  }

  // ============================================================================
  // DASHBOARD & ANALYTICS
  // ============================================================================

  async getDashboardStats(): Promise<DashboardStats> {
    const response = await this.api.get('/dashboard/stats');
    return response.data.data;
  }

  async getPlatformAnalytics(): Promise<PlatformAnalytics> {
    const response = await this.api.get('/analytics');
    return response.data.data;
  }

  async getSystemHealth(): Promise<any> {
    const response = await this.api.get('/system/health');
    return response.data.data;
  }

  // ============================================================================
  // USER MANAGEMENT
  // ============================================================================

  async getAllUsers(params?: {
    page?: number;
    limit?: number;
    userType?: string;
    verified?: boolean;
    search?: string;
  }): Promise<any> {
    const response = await this.api.get('/users', { params });
    return response.data.data;
  }

  async getUserDetails(userId: string): Promise<any> {
    const response = await this.api.get(`/users/${userId}`);
    return response.data.data;
  }

  async updateUser(userId: string, updates: any): Promise<ApiResponse> {
    const response = await this.api.put(`/users/${userId}`, updates);
    return response.data;
  }

  async suspendUser(userId: string, reason: string): Promise<ApiResponse> {
    const response = await this.api.post(`/users/${userId}/suspend`, { reason });
    return response.data;
  }

  async unsuspendUser(userId: string): Promise<ApiResponse> {
    const response = await this.api.post(`/users/${userId}/unsuspend`);
    return response.data;
  }

  async deleteUser(userId: string): Promise<ApiResponse> {
    const response = await this.api.delete(`/users/${userId}`);
    return response.data;
  }

  async resetUserPassword(userId: string): Promise<ApiResponse> {
    const response = await this.api.post(`/users/${userId}/reset-password`);
    return response.data;
  }

  // ============================================================================
  // JOB MANAGEMENT
  // ============================================================================

  async getAllJobs(params?: {
    page?: number;
    limit?: number;
    status?: string;
    employerId?: string;
  }): Promise<any> {
    const response = await this.api.get('/jobs', { params });
    return response.data.data;
  }

  async approveJob(jobId: string): Promise<ApiResponse> {
    const response = await this.api.post(`/jobs/${jobId}/approve`);
    return response.data;
  }

  async rejectJob(jobId: string, reason: string): Promise<ApiResponse> {
    const response = await this.api.post(`/jobs/${jobId}/reject`, { reason });
    return response.data;
  }

  async deleteJob(jobId: string): Promise<ApiResponse> {
    const response = await this.api.delete(`/jobs/${jobId}`);
    return response.data;
  }

  // ============================================================================
  // COURSE MANAGEMENT
  // ============================================================================

  async getAllCourses(params?: {
    page?: number;
    limit?: number;
    category?: string;
  }): Promise<any> {
    const response = await this.api.get('/courses', { params });
    return response.data.data;
  }

  async createCourse(courseData: any): Promise<ApiResponse> {
    const response = await this.api.post('/courses', courseData);
    return response.data;
  }

  async updateCourse(courseId: string, updates: any): Promise<ApiResponse> {
    const response = await this.api.put(`/courses/${courseId}`, updates);
    return response.data;
  }

  async deleteCourse(courseId: string): Promise<ApiResponse> {
    const response = await this.api.delete(`/courses/${courseId}`);
    return response.data;
  }

  async createCourseFromTemplate(templateData: any): Promise<ApiResponse> {
    const response = await this.api.post('/courses', templateData);
    return response.data;
  }


  // ============================================================================
  // VERIFICATION MANAGEMENT
  // ============================================================================

  async getVerificationQueue(params?: {
    type?: 'identity' | 'business';
    status?: 'pending' | 'approved' | 'rejected';
  }): Promise<any> {
    const response = await this.api.get('/verifications/queue', { params });
    return response.data.data;
  }

  async approveVerification(verificationId: string, notes?: string): Promise<ApiResponse> {
    const response = await this.api.post(`/verifications/${verificationId}/approve`, { notes });
    return response.data;
  }

  async rejectVerification(verificationId: string, reason: string): Promise<ApiResponse> {
    const response = await this.api.post(`/verifications/${verificationId}/reject`, { reason });
    return response.data;
  }

  // ============================================================================
  // CONTENT MODERATION
  // ============================================================================

  async getModerationQueue(page = 1, limit = 50, contentType?: string, status?: string, priority?: string): Promise<ApiResponse> {
    const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
    if (contentType) params.append('contentType', contentType);
    if (status) params.append('status', status);
    if (priority) params.append('priority', priority);
    
    const response = await this.api.get(`/moderation/content?${params}`);
    return response.data;
  }

  async moderateContent(itemId: string, action: 'approved' | 'rejected' | 'flagged' | 'dismissed', reason?: string, notes?: string): Promise<ApiResponse> {
    const response = await this.api.post(`/moderation/content/${itemId}`, { action, reason, notes });
    return response.data;
  }

  async getModerationItem(itemId: string): Promise<ApiResponse> {
    const response = await this.api.get(`/moderation/content/${itemId}`);
    return response.data;
  }

  // ============================================================================
  // BACKGROUND CHECKS
  // ============================================================================

  async getBackgroundChecks(page = 1, limit = 50, checkType?: string, status?: string, riskLevel?: string): Promise<ApiResponse> {
    const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
    if (checkType) params.append('checkType', checkType);
    if (status) params.append('status', status);
    if (riskLevel) params.append('riskLevel', riskLevel);
    
    try {
      // Try to get real background checks data - this endpoint may not exist yet
      const response = await this.api.get(`/background-checks?${params}`);
      return response.data;
    } catch (error: any) {
      // If endpoint doesn't exist, return empty but successful response
      if (error.response?.status === 404) {
        return {
          success: true,
          data: {
            backgroundChecks: [],
            total: 0,
            summary: {
              pending: 0,
              completed: 0,
              failed: 0,
              highRisk: 0,
              totalChecks: 0,
              averageProcessingTime: 0
            }
          },
          message: 'Background checks feature not fully implemented yet'
        };
      }
      throw error;
    }
  }

  async reviewBackgroundCheck(checkId: string, action: 'approve' | 'flag', notes?: string): Promise<ApiResponse> {
    try {
      const response = await this.api.post(`/background-checks/${checkId}/review`, { action, notes });
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return {
          success: false,
          message: 'Background check review feature not fully implemented yet'
        };
      }
      throw error;
    }
  }

  // ============================================================================
  // SYSTEM MANAGEMENT
  // ============================================================================

  async getSystemSettings(): Promise<any> {
    const response = await this.api.get('/system/settings');
    return response.data.data;
  }

  async updateSystemSettings(settings: any): Promise<ApiResponse> {
    const response = await this.api.put('/system/settings', settings);
    return response.data;
  }

  async getSystemLogs(params?: {
    level?: 'info' | 'warn' | 'error';
    startDate?: string;
    endDate?: string;
  }): Promise<any> {
    const response = await this.api.get('/system/logs', { params });
    return response.data.data;
  }

  async createBackup(): Promise<ApiResponse> {
    const response = await this.api.post('/system/backup');
    return response.data;
  }

  async getBackups(): Promise<any> {
    const response = await this.api.get('/system/backups');
    return response.data.data;
  }

  // ============================================================================
  // REPORTS
  // ============================================================================

  async generateReport(dataType: string, format: string, params?: any): Promise<ApiResponse> {
    try {
      const response = await this.api.post('/export', { 
        dataType, 
        format, 
        filters: params?.filters,
        dateRange: params?.dateRange 
      });
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return {
          success: false,
          message: 'Report generation feature not fully implemented yet'
        };
      }
      throw error;
    }
  }

  async getReports(page = 1, limit = 50): Promise<ApiResponse> {
    const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
    
    try {
      const response = await this.api.get(`/exports?${params}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return {
          success: true,
          data: {
            exports: [],
            total: 0
          },
          message: 'Reports feature not fully implemented yet'
        };
      }
      throw error;
    }
  }

  async getReportStatus(exportId: string): Promise<ApiResponse> {
    try {
      const response = await this.api.get(`/export/${exportId}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return {
          success: false,
          message: 'Report status feature not fully implemented yet'
        };
      }
      throw error;
    }
  }

  async downloadReport(exportId: string): Promise<Blob> {
    const response = await this.api.get(`/export/${exportId}/download`, {
      responseType: 'blob'
    });
    return response.data;
  }

  // ============================================================================
  // ADMIN MANAGEMENT
  // ============================================================================

  async getAllAdmins(): Promise<any> {
    const response = await this.api.get('/admins');
    return response.data.data;
  }

  async createAdmin(adminData: any): Promise<ApiResponse> {
    const response = await this.api.post('/admins', adminData);
    return response.data;
  }

  async updateAdminRole(adminId: string, role: string, permissions: string[]): Promise<ApiResponse> {
    const response = await this.api.put(`/admins/${adminId}/role`, { role, permissions });
    return response.data;
  }

  async deactivateAdmin(adminId: string): Promise<ApiResponse> {
    const response = await this.api.post(`/admins/${adminId}/deactivate`);
    return response.data;
  }

  // ============================================================================
  // SUBSCRIPTIONS & BILLING
  // ============================================================================

  async getSubscriptions(page = 1, limit = 50, status?: string, tier?: string): Promise<ApiResponse> {
    const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
    if (status) params.append('status', status);
    if (tier) params.append('tier', tier);
    
    try {
      // Try to get real subscription data - this endpoint may not exist yet
      const response = await this.api.get(`/subscriptions?${params}`);
      return response.data;
    } catch (error: any) {
      // If endpoint doesn't exist, return empty but successful response
      if (error.response?.status === 404) {
        return {
          success: true,
          data: {
            subscriptions: [],
            total: 0,
            summary: {
              totalSubscriptions: 0,
              activeSubscriptions: 0,
              monthlyRecurringRevenue: 0,
              tierDistribution: { basic: 0, premium: 0, enterprise: 0 },
              totalJobPostingsUsed: 0
            }
          },
          message: 'Subscription management feature not fully implemented yet'
        };
      }
      throw error;
    }
  }

  async updateSubscription(subscriptionId: string, updates: any): Promise<ApiResponse> {
    try {
      const response = await this.api.put(`/subscriptions/${subscriptionId}`, updates);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return {
          success: false,
          message: 'Subscription update feature not fully implemented yet'
        };
      }
      throw error;
    }
  }

  async cancelSubscription(subscriptionId: string, reason?: string): Promise<ApiResponse> {
    try {
      const response = await this.api.post(`/subscriptions/${subscriptionId}/cancel`, { reason });
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return {
          success: false,
          message: 'Subscription cancellation feature not fully implemented yet'
        };
      }
      throw error;
    }
  }

  // ============================================================================
  // ADDITIONAL ADMIN FUNCTIONS
  // ============================================================================

  async getCourses(): Promise<ApiResponse> {
    const response = await this.api.get('/courses');
    return response.data;
  }

  async getJobs(page = 1, limit = 50, status?: string, company?: string, location?: string): Promise<ApiResponse> {
    const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
    if (status) params.append('status', status);
    if (company) params.append('company', company);
    if (location) params.append('location', location);
    
    const response = await this.api.get(`/jobs?${params}`);
    return response.data;
  }

  async moderateJob(jobId: string, action: string, reason?: string, notes?: string): Promise<ApiResponse> {
    const response = await this.api.post(`/jobs/${jobId}/moderate`, { action, reason, notes });
    return response.data;
  }

  async updateJob(jobId: string, updates: any): Promise<ApiResponse> {
    const response = await this.api.put(`/jobs/${jobId}`, updates);
    return response.data;
  }

  async getVerifications(): Promise<ApiResponse> {
    const response = await this.api.get('/verifications');
    return response.data;
  }

  async getAISystemStatus(): Promise<ApiResponse> {
    const response = await this.api.get('/ai-system');
    return response.data;
  }

  async getAnalytics(period = '6months', startDate?: string, endDate?: string): Promise<ApiResponse> {
    const params = new URLSearchParams({ period });
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    const response = await this.api.get(`/analytics?${params}`);
    return response.data;
  }

  // ============================================================================
  // EMAIL MANAGEMENT
  // ============================================================================

  async getEmailTemplates(): Promise<any[]> {
    const response = await this.api.get('/emails/templates');
    return response.data.data || [];
  }

  async getEmailHistory(): Promise<any[]> {
    const response = await this.api.get('/emails/history');
    return response.data.data || [];
  }

  async sendBulkEmail(emailData: {
    subject: string;
    content: string;
    recipientType: 'all' | 'students' | 'employers' | 'specific';
    specificRecipients?: string[];
    templateId?: string;
    scheduledDate?: string;
  }): Promise<ApiResponse> {
    const response = await this.api.post('/emails/send-bulk', emailData);
    return response.data;
  }

  async createEmailTemplate(template: {
    name: string;
    subject: string;
    content: string;
    type: 'announcement' | 'notification' | 'marketing' | 'system';
  }): Promise<ApiResponse> {
    const response = await this.api.post('/emails/templates', template);
    return response.data;
  }

  async updateEmailTemplate(templateId: string, template: {
    name?: string;
    subject?: string;
    content?: string;
    type?: 'announcement' | 'notification' | 'marketing' | 'system';
  }): Promise<ApiResponse> {
    const response = await this.api.put(`/emails/templates/${templateId}`, template);
    return response.data;
  }

  async deleteEmailTemplate(templateId: string): Promise<ApiResponse> {
    const response = await this.api.delete(`/emails/templates/${templateId}`);
    return response.data;
  }

  // ============================================================================
  // SUPPORT TICKETS
  // ============================================================================

  async getSupportTickets(params?: {
    status?: string;
    priority?: string;
    category?: string;
    assignedTo?: string;
    page?: number;
    limit?: number;
  }): Promise<any[]> {
    const response = await this.api.get('/support/tickets', { params });
    return response.data.data || [];
  }

  async getSupportTicketStats(): Promise<any> {
    const response = await this.api.get('/support/stats');
    return response.data.data;
  }

  async getSupportTicket(ticketId: string): Promise<any> {
    const response = await this.api.get(`/support/tickets/${ticketId}`);
    return response.data.data;
  }

  async updateTicketStatus(ticketId: string, status: string): Promise<ApiResponse> {
    const response = await this.api.put(`/support/tickets/${ticketId}/status`, { status });
    return response.data;
  }

  async assignTicket(ticketId: string, adminId?: string): Promise<ApiResponse> {
    const response = await this.api.put(`/support/tickets/${ticketId}/assign`, { adminId });
    return response.data;
  }

  async respondToTicket(ticketId: string, responseData: {
    message: string;
    isInternal?: boolean;
    newStatus?: string;
    attachments?: File[];
  }): Promise<ApiResponse> {
    const response = await this.api.post(`/support/tickets/${ticketId}/respond`, responseData);
    return response.data;
  }

  async createSupportTicket(ticketData: {
    subject: string;
    description: string;
    priority: 'low' | 'medium' | 'high' | 'urgent';
    category: 'technical' | 'billing' | 'feature-request' | 'bug-report' | 'general';
    userId: string;
  }): Promise<ApiResponse> {
    const response = await this.api.post('/support/tickets', ticketData);
    return response.data;
  }

  async closeSupportTicket(ticketId: string, reason?: string): Promise<ApiResponse> {
    const response = await this.api.put(`/support/tickets/${ticketId}/close`, { reason });
    return response.data;
  }

  async getSupportTicketMetrics(params?: {
    startDate?: string;
    endDate?: string;
    adminId?: string;
  }): Promise<any> {
    const response = await this.api.get('/support/metrics', { params });
    return response.data.data;
  }

  // ============================================================================
  // ADMIN PERMISSION MIGRATION
  // ============================================================================

  async migrateAdminPermissions(): Promise<ApiResponse> {
    const response = await this.api.post('/migrate-permissions');
    return response.data;
  }

  async createSampleData(): Promise<ApiResponse> {
    const response = await this.api.post('/create-sample-data');
    return response.data;
  }

  // System Configuration
  async getSystemConfiguration(): Promise<ApiResponse> {
    const response = await this.api.get('/config');
    return response.data;
  }

  async updateSystemConfiguration(config: any): Promise<ApiResponse> {
    const response = await this.api.put('/config', config);
    return response.data;
  }

  async resetSystemConfiguration(): Promise<ApiResponse> {
    const response = await this.api.post('/config/reset');
    return response.data;
  }

  // Admin Management
  async getAdminUsers(page = 1, limit = 20, role?: string, isActive?: string): Promise<ApiResponse> {
    const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
    if (role) params.append('role', role);
    if (isActive) params.append('isActive', isActive);
    
    const response = await this.api.get(`/admins?${params}`);
    return response.data;
  }

  async createAdminUser(adminData: any): Promise<ApiResponse> {
    const response = await this.api.post('/admins', adminData);
    return response.data;
  }

  async updateAdminUser(adminId: string, updates: any): Promise<ApiResponse> {
    const response = await this.api.put(`/admins/${adminId}`, updates);
    return response.data;
  }

  async deleteAdminUser(adminId: string): Promise<ApiResponse> {
    const response = await this.api.delete(`/admins/${adminId}`);
    return response.data;
  }

  async getAdminActivity(page = 1, limit = 50, adminId?: string, action?: string): Promise<ApiResponse> {
    const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
    if (adminId) params.append('adminId', adminId);
    if (action) params.append('action', action);
    
    const response = await this.api.get(`/admin-activity?${params}`);
    return response.data;
  }

  // ============================================================================
  // SUBSCRIPTION MANAGEMENT
  // ============================================================================

  async getSubscriptionPlans(): Promise<ApiResponse> {
    const response = await this.api.get('/subscription-plans');
    return response.data;
  }

  async createSubscriptionPlan(planData: any): Promise<ApiResponse> {
    const response = await this.api.post('/subscription-plans', planData);
    return response.data;
  }

  async updateSubscriptionPlan(planId: string, updates: any): Promise<ApiResponse> {
    const response = await this.api.put(`/subscription-plans/${planId}`, updates);
    return response.data;
  }

  async getSubscriptionAnalytics(period = '30d', startDate?: string, endDate?: string): Promise<ApiResponse> {
    const params = new URLSearchParams({ period });
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await this.api.get(`/subscription-analytics?${params}`);
    return response.data;
  }

  async getUserSubscriptions(page = 1, limit = 50, status?: string, planId?: string): Promise<ApiResponse> {
    const params = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
    if (status) params.append('status', status);
    if (planId) params.append('planId', planId);

    const response = await this.api.get(`/user-subscriptions?${params}`);
    return response.data;
  }

  async createDefaultSubscriptionPlans(): Promise<ApiResponse> {
    const response = await this.api.post('/subscription-plans/create-defaults');
    return response.data;
  }

  async createSampleNotifications(): Promise<ApiResponse> {
    const response = await this.api.post('/notifications/create-samples');
    return response.data;
  }

  async getCurrentAdminStatus(): Promise<ApiResponse> {
    const response = await this.api.get('/current-admin');
    return response.data;
  }

  // Contact Inquiries Methods
  async getContactInquiries(params?: {
    status?: string;
    assignedTo?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<any> {
    const response = await this.api.get('/contact/inquiries', { params });
    return response.data;
  }

  async getContactInquiryStats(): Promise<any> {
    const response = await this.api.get('/contact/stats');
    return response.data;
  }

  async getContactInquiry(inquiryId: string): Promise<any> {
    const response = await this.api.get(`/contact/inquiries/${inquiryId}`);
    return response.data;
  }

  async updateContactInquiryStatus(inquiryId: string, status: string): Promise<ApiResponse> {
    const response = await this.api.put(`/contact/inquiries/${inquiryId}/status`, { status });
    return response.data;
  }

  async assignContactInquiry(inquiryId: string, adminId?: string): Promise<ApiResponse> {
    const response = await this.api.put(`/contact/inquiries/${inquiryId}/assign`, { adminId });
    return response.data;
  }

  async respondToContactInquiry(inquiryId: string, responseData: {
    message: string;
    newStatus?: string;
  }): Promise<ApiResponse> {
    const response = await this.api.post(`/contact/inquiries/${inquiryId}/respond`, responseData);
    return response.data;
  }
}

export default new AdminApiService(); 